import pickBy from 'lodash/pickBy';
import APIUtils from '@spothero/utils/api';
import EnvironmentUtils from '@spothero/utils/environment';
import OptimizelyUtils from '@spothero/utils/optimizely';
import SegmentUtils from 'utils/segment';
import GTMUtils from 'utils/gtm';
import ErrorUtils from 'utils/error-utils';
import StorageUtils from '@spothero/utils/storage';
import Config from '@/config/index';
import { getWindow } from 'ssr-window';
// TODO MR - This is giving issues related to onload
export const IMMEDIATE_FEATURES = {
    SMS_OPT_IN_CHECKOUT: 'sms_opt_in_checkout',
    ENABLE_POWER_BOOKING: 'enable_power_booking',
    POWER_BOOKING_CHECKOUT_V2: 'power_booking_checkout_v2',
    AIRPORT_CHECKOUT_V2: 'airport_checkout_v2',
    AIRPORT_INSURANCE_UPSELL: 'airport_insurance_upsell',
    USE_EVENT_PACKAGE_V2_SEARCH: 'use_event_package_v2_search',
    HPE_CANCELLATION_UPDATES: 'hpe_cancellation_updates',
    USE_MONTHLY_V2_SEARCH_FACILITY_FOR_CHECKOUT: 'use_monthly_v2_search_facility_for_checkout',
    PARKING_PASS_TIME_ADJUSTMENTS: 'parking_pass_time_adjustments',
    OVERSIZED_PARKING_PERSONALIZATION: 'oversized_parking_personalization',
    ACQUISITION_TOTAL_PRICE: 'acquisition_total_price',
    ACQUISITION_TOTAL_PRICE_MONTHLY: 'acquisition_total_price_monthly',
    PARKCHIP_TERMS_OF_SERVICE: 'parkchip_terms_of_service',
};
// Define variables by feature in the format `[variableName, type]`
export const VARIABLES = {};
export const IMMEDIATE_AB_EXPERIMENTS = {};
const NO_VARIATION = 'a';
const window = getWindow();
const isBrowser = EnvironmentUtils.isBrowser();
const EXPERIMENT_OVERRIDE_PREFIX = 'SH_EXPERIMENT_OVERRIDE';
let experimentOverrideServerCookies;
export const trackOptimizelyExperimentsActivationsInSegment = (experimentName, variationName) => {
    // only track active Optimizely experiments
    if (variationName !== NO_VARIATION) {
        let experimentId;
        let variationId;
        try {
            experimentId = window.shOptimizelyActivations[experimentName].id;
            variationId = window.shOptimizelyActivations[experimentName].variations.find(v => v.key === variationName).id;
        }
        catch (e) {
            console.error('Failed to parse Optimizely experiment values'); // eslint-disable-line no-console
        }
        SegmentUtils.track({
            event: 'Experiment Viewed',
            properties: {
                /* eslint-disable camelcase */
                experiment_id: experimentId,
                experiment_name: experimentName,
                variation_id: variationId,
                variation_name: variationName,
                /* eslint-enable camelcase */
            },
        });
    }
};
/**
 * Utilities for working with Optimizely.
 *
 * @module ExperimentUtils
 */
const ExperimentUtils = {
    async init({ isDev = false, optimizelyData = null, serverCookies = null, user = null, onLoad, } = {}) {
        experimentOverrideServerCookies = pickBy(serverCookies, (value, key) => key.startsWith(EXPERIMENT_OVERRIDE_PREFIX));
        let datafile;
        try {
            const optimizelyResponse = await OptimizelyUtils.initialize(optimizelyData, {
                isDev,
                isBrowser,
                serverCookies,
                isBot: Config.isBot,
                loadMechanism: APIUtils.get,
                onDatafileSuccess: this.clearVariationsCookie,
                onDatafileError: error => {
                    ErrorUtils.sendSentryMessage({
                        error,
                        customErrorMessage: 'Optimizely datafile error',
                    });
                    this.clearClient();
                },
                onActivate: payload => {
                    const { experiment } = payload;
                    if (typeof window.shOptimizelyActivations ===
                        'undefined') {
                        window.shOptimizelyActivations = {};
                    }
                    window.shOptimizelyActivations[experiment.key] = experiment;
                },
            });
            datafile = optimizelyResponse.datafile;
        }
        catch (error) {
            ErrorUtils.sendSentryMessage({
                error,
                customErrorMessage: 'Optimizely initialization error',
            });
        }
        this.initUserAttributes(user);
        if (onLoad) {
            onLoad();
        }
        return optimizelyData || datafile;
    },
    /**
     * Checks Optimizely to see if the user is bucketed in this experiment
     * Activates the experiment for the current session; an impression will be sent to Optimizely
     * Appends the experiment name and variation name to a cookie (used by analytics and the Reservations API)
     *
     * @static
     * @function activate
     * @param {string} experimentName - Experiment name
     * @param {object} options - Options to use
     * @param {boolean} options.sendToAnalytics - Whether or not to send experiment information to Segment and GTM (defaults to true)
     * @example ExperimentUtils.activate('mw_checkout_value_props') => 'vp_below';
     * @example ExperimentUtils.activate('mw_checkout_value_props', {sendToAnalytics: false}) => 'vp_below';
     * @returns {string} variationName - For each variation, returns the name of the variation assigned to this user, or 'a'
     */
    activate(experimentName, { sendToAnalytics = true } = {}) {
        if (!Config.isBot && !Config.runningTests) {
            const variationName = OptimizelyUtils.activate(experimentName);
            if (!variationName || variationName === NO_VARIATION) {
                return NO_VARIATION;
            }
            trackOptimizelyExperimentsActivationsInSegment(experimentName, variationName);
            if (sendToAnalytics) {
                this.sendToAnalytics();
            }
            return variationName;
        }
        return NO_VARIATION;
    },
    /**
     * Checks if a feature is enabled for a user
     *
     * @static
     * @function isFeatureEnabled
     * @param {string} featureName - The name of the feature
     * @param {object} options - Options to use
     * @param {boolean} options.sendToAnalytics - Whether to send experiment information to Segment and GTM (defaults to false)
     * @example ExperimentUtils.isFeatureEnabled('web_canada');
     * @example ExperimentUtils.isFeatureEnabled('web_canada', {sendToAnalytics: true});
     * @returns {boolean} isEnabled - Whether the feature is enabled for this user
     */
    isFeatureEnabled(featureName, { sendToAnalytics = false } = {}) {
        if (!Config.isProduction) {
            // For testing purposes, allow a feature to be force enabled (or disabled) by setting
            // a cookie named "SH_EXPERIMENT_OVERRIDE_{featureName}" equal to a boolean value
            // (boolean true to force enable or boolean false to force disable)
            const overrideValue = getExperimentOverride(featureName);
            if (overrideValue !== null) {
                return overrideValue;
            }
        }
        if (!Config.isBot && !Config.runningTests) {
            const isFeatureEnabled = OptimizelyUtils.isFeatureEnabled(featureName);
            if (isFeatureEnabled && sendToAnalytics) {
                this.sendToAnalytics();
            }
            return isFeatureEnabled;
        }
        return false;
    },
    /**
     * Checks Optimizely to see if the user is bucketed in this experiment
     * Does not activate the experiment for the current session; no impression will be sent to Optimizely
     *
     * @static
     * @function getVariation
     * @param {string} experimentName - Name of experiment
     * @example ExperimentUtils.getVariation('homepage_tabbed_search') => 'tabbed_with_value_props';
     * @example ExperimentUtils.getVariation('homepage_tabbed_search', 'mw_reno_spot') => {
     *   homepage_tabbed_search: 'tabbed_with_value_props',
     *   mw_reno_spot: 'variation2',
     * };
     * @returns {string | object} variationName - For each variation, returns the name of the variation assigned to this user, or 'a'
     * If a single argument is passed, the variation name is returned as a string
     * If multiple arguments are passed, experiment name/variation name key/value pairs are returned
     */
    getVariation(experimentName) {
        if (!Config.isBot && !Config.runningTests) {
            return OptimizelyUtils.getVariation(experimentName);
        }
        return NO_VARIATION;
    },
    /**
     * @static
     * @function getFeatureVariable
     * @param {object} requiredParams - All params are required
     * @param {string} requiredParams.featureName - The name of the feature
     * @param {string} requiredParams.variableName - The name of the feature variable
     * @param {string} requiredParams.type - The type of the feature variable
     * @example ExperimentUtils.getFeatureVariable({
     *      featureName: 'web_canada',
     *      variableName: 'search_enabled',
     *      type: 'Boolean',
     * });
     * @returns {number | string | boolean} variableValue - The value of the given variable and type for the given feature
     */
    getFeatureVariable(requiredParams) {
        if (!Config.isBot && !Config.runningTests) {
            return OptimizelyUtils.getFeatureVariable(requiredParams);
        }
        return false;
    },
    /**
     * Sends experiment data to Segment and GTM
     *
     * @static
     * @function sendToAnalytics
     * @returns {void}
     */
    sendToAnalytics() {
        if (Config.isDeployed && isBrowser) {
            const variationsCookie = OptimizelyUtils.getVariationsCookie();
            SegmentUtils.addTraits({
                experiments: OptimizelyUtils.getSegmentExperiments(),
            });
            GTMUtils.push({
                event: 'experimentVariationsUpdated',
                experimentVariations: JSON.stringify(variationsCookie),
            });
        }
    },
    /**
     * Initializes user attributes with device and environment data
     *
     * @static
     * @function initUserAttributes
     * @param {object} user - The user information to derive attributes from.
     * @returns {void}
     */
    initUserAttributes(user = { email: undefined, isAdmin: undefined, id: undefined, monthly: undefined }) {
        const userAttributes = {
            client: Config.isMobile ? 'mobile-web' : 'web',
            environment: Config.isProduction ? 'production' : 'staging',
            email: user?.email || null,
            staff: Boolean(user && user.isAdmin).toString(),
            shUserId: user?.id || null,
            isBrowser,
            isBot: Config.isBot,
            pageUrl: window?.location?.href,
            pagePath: window?.location?.pathname,
            pageReferrer: window?.document?.referrer || null,
            userAgent: window?.navigator?.userAgent,
            browser: EnvironmentUtils.getBrowserCategory(),
            monthly: user?.monthly || null,
            // ...(Config.clientIPAddress && {
            //     ip: Config.clientIPAddress,
            // }),
        };
        OptimizelyUtils.setUserAttributes(userAttributes, { reset: true });
    },
    /**
     * A single location to bucket experiments and features needed on app load
     * Formats results for retrieval with redux (store.user)
     *
     * @static
     * @function getBuckets
     * @param {object} experimentObj - Dictionary of constant and Optimizely experiment name key/value pairs
     * @param {object} featuresObj - Dictionary of constant and Optimizely feature flag name key/value pairs
     * @returns {object} - Formatted experiment values for injecting into the redux (store.user)
     */
    getBuckets(experimentObj = IMMEDIATE_AB_EXPERIMENTS, featuresObj = IMMEDIATE_FEATURES) {
        const experiments = Object.keys(experimentObj)
            .map(experimentName => experimentObj[experimentName])
            .reduce((variations, experimentName) => ({
            ...variations,
            [experimentName]: this.activate(experimentName, {
                sendToAnalytics: false,
            }),
        }), {});
        const features = Object.keys(featuresObj)
            .map(featureName => featuresObj[featureName])
            .reduce((enabledFeatures, featureName) => {
            const variables = VARIABLES[featureName];
            const isFeatureEnabled = this.isFeatureEnabled(featureName, {
                sendToAnalytics: false,
            });
            return {
                ...enabledFeatures,
                [featureName]: {
                    isFeatureEnabled,
                    ...(variables &&
                        OptimizelyUtils.getEnabledFeatureVariables(featureName, variables)),
                },
            };
        }, {});
        return {
            ...experiments,
            ...features,
        };
    },
};
/**
 * Returns the value of the experiment override cookie for the given feature name or null
 * if there isn't one.
 *
 * @param {string} featureName - The name of the feature.
 * @returns {boolean | null} - A boolean indicating the overridden value or null otherwise.
 */
function getExperimentOverride(featureName) {
    const cookieName = `${EXPERIMENT_OVERRIDE_PREFIX}_${featureName}`;
    let cookieVal = null;
    if (experimentOverrideServerCookies &&
        cookieName in experimentOverrideServerCookies) {
        cookieVal = experimentOverrideServerCookies[cookieName];
        if (cookieVal === '0' || cookieVal === 'false') {
            cookieVal = false;
        }
    }
    else {
        cookieVal = StorageUtils.get(cookieName, 'cookie');
    }
    return cookieVal === null ? null : Boolean(cookieVal);
}
export default {
    ...OptimizelyUtils,
    ...ExperimentUtils,
};
